import React from 'react';
import { ButtonText } from '@morressier/alembic';
import { downloadFile, getFileExtension } from 'src/utils/file';
import { createFileViewerRequest } from 'src/utils/fileViewer';
import { ISubmissionFile } from '@publishing-engine/domain/models';

type PreviewableFile = {
  url: string;
  extension?: string | null;
  name?: string | null;
};

type Props = Omit<React.ComponentProps<typeof ButtonText>, 'onClick'> & {
  file: PreviewableFile | ISubmissionFile;
  submissionContextId?: string;
  organizationId?: string;
  onClick?: () => void;
};

export const getPreviewExtension = (file: PreviewableFile): string => {
  return (file.extension || getFileExtension(file.url) || '').toLowerCase().trim();
};

export const canPreviewInFileViewer = (file: PreviewableFile): boolean => {
  const extension = getPreviewExtension(file);
  return extension === 'pdf' || extension === 'doc' || extension === 'docx';
};

const FileViewerButtonText: React.FC<Props> = ({
  file,
  submissionContextId,
  organizationId,
  onClick,
  children,
  ...buttonProps
}) => {
  const extension = getPreviewExtension(file);
  const canPreview = canPreviewInFileViewer(file);

  const handleClick = () => {
    onClick?.();

    if (!canPreview) {
      downloadFile(file.url, { submissionContextId, organizationId });
      return;
    }

    const requestId = createFileViewerRequest({
      fileUrl: file.url,
      fileExtension: extension,
      submissionContextId,
      organizationId
    });

    window.open(
      `/file-viewer?requestId=${encodeURIComponent(requestId)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <ButtonText {...buttonProps} onClick={handleClick}>
      {children ?? file.name}
    </ButtonText>
  );
};

export default FileViewerButtonText;
